/**
 * Cognito Hosted UI OAuth flow with PKCE — mirrors the Otto dashboard auth pattern.
 *
 * Flow:
 *   1. startSignIn() generates a PKCE verifier, stores it in localStorage, and redirects
 *      to the Cognito Hosted UI with the Office 365 SAML IdP pre-selected. The user signs
 *      in with their TradeBid staff credentials, Cognito mints tokens, and redirects to
 *      /oauth/callback with an authorization code.
 *   2. exchangeCodeForTokens(code) on the callback page swaps the code for ID + access +
 *      refresh tokens, using the stored PKCE verifier as proof of possession.
 *   3. getValidAccessToken() returns the current access token, refreshing it transparently
 *      via the refresh token if it's expired.
 *
 * Tokens are stored in sessionStorage (cleared on tab close) rather than localStorage
 * to limit blast radius if the dashboard is left open on a shared machine.
 */
import { getConfig } from './config';

const TOKEN_KEY = 'obd_dashboard_access_token';
const ID_TOKEN_KEY = 'obd_dashboard_id_token';
const REFRESH_TOKEN_KEY = 'obd_dashboard_refresh_token';
const PKCE_VERIFIER_KEY = 'obd_dashboard_pkce_verifier';

interface TokenSet {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_in: number;
}

interface JwtPayload {
  sub?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  exp?: number;
  [key: string]: unknown;
}

function parseJwt(token: string): JwtPayload {
  const base64Url = token.split('.')[1];
  if (!base64Url) return {};
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join(''),
  );
  return JSON.parse(json) as JwtPayload;
}

function buildPkceVerifier(): string {
  const verifier = Array.from(crypto.getRandomValues(new Uint8Array(32)))
    .map((n) => n.toString(16).padStart(2, '0'))
    .join('');
  localStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  return verifier;
}

async function buildPkceChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function consumePkceVerifier(): string | null {
  const verifier = localStorage.getItem(PKCE_VERIFIER_KEY);
  localStorage.removeItem(PKCE_VERIFIER_KEY);
  return verifier;
}

export async function startSignIn(): Promise<void> {
  const config = getConfig();
  const redirectUri = `${window.location.origin}/oauth/callback`;
  const verifier = buildPkceVerifier();
  const challenge = await buildPkceChallenge(verifier);

  const params = new URLSearchParams({
    response_type: config.auth.responseType,
    client_id: config.auth.clientId,
    redirect_uri: redirectUri,
    scope: config.auth.scope,
    identity_provider: config.auth.identityProvider,
    code_challenge_method: 'S256',
    code_challenge: challenge,
  });

  window.location.assign(
    `${config.auth.cognitoHostedUiBaseUrl}/oauth2/authorize?${params.toString()}`,
  );
}

export function getLogoutUrl(): string {
  const config = getConfig();
  const logoutUri = `${window.location.origin}/`;
  const params = new URLSearchParams({
    client_id: config.auth.clientId,
    logout_uri: logoutUri,
  });
  return `${config.auth.cognitoHostedUiBaseUrl}/logout?${params.toString()}`;
}

export async function exchangeCodeForTokens(code: string): Promise<void> {
  const config = getConfig();
  const redirectUri = `${window.location.origin}/oauth/callback`;
  const verifier = consumePkceVerifier();

  const bodyParams: Record<string, string> = {
    grant_type: 'authorization_code',
    client_id: config.auth.clientId,
    redirect_uri: redirectUri,
    code,
  };
  if (verifier) bodyParams.code_verifier = verifier;
  const body = new URLSearchParams(bodyParams);

  const response = await fetch(`${config.auth.cognitoHostedUiBaseUrl}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!response.ok) throw new Error(`Token exchange failed: ${response.status}`);

  const tokens = (await response.json()) as TokenSet;
  sessionStorage.setItem(TOKEN_KEY, tokens.access_token);
  sessionStorage.setItem(ID_TOKEN_KEY, tokens.id_token);
  if (tokens.refresh_token) sessionStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
}

export function getAccessToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function getIdToken(): string | null {
  return sessionStorage.getItem(ID_TOKEN_KEY);
}

function isTokenExpired(token: string): boolean {
  try {
    const payload = parseJwt(token);
    if (!payload.exp) return true;
    return payload.exp * 1000 <= Date.now() + 60_000;
  } catch {
    return true;
  }
}

let refreshPromise: Promise<boolean> | null = null;

export async function refreshAccessToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) return false;
    const config = getConfig();
    try {
      const body = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: config.auth.clientId,
        refresh_token: refreshToken,
      });
      const response = await fetch(`${config.auth.cognitoHostedUiBaseUrl}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!response.ok) return false;
      const tokens = (await response.json()) as TokenSet;
      sessionStorage.setItem(TOKEN_KEY, tokens.access_token);
      sessionStorage.setItem(ID_TOKEN_KEY, tokens.id_token);
      if (tokens.refresh_token) sessionStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export async function getValidAccessToken(): Promise<string | null> {
  const token = getAccessToken();
  if (!token) return null;
  if (!isTokenExpired(token)) return token;
  const refreshed = await refreshAccessToken();
  return refreshed ? getAccessToken() : null;
}

/**
 * Returns the ID token (with email + given_name + family_name claims),
 * refreshing transparently if expired. Used as the Bearer token for
 * admin-api calls so the backend can stamp reviewer identity on
 * audit fields without needing a separate request-body parameter.
 *
 * Why ID token and not access token: access tokens are scoped for the
 * authorization server's token endpoint and carry only `sub` / `aud` /
 * `scope` — no user-profile claims. ID tokens carry the full profile.
 * Both have `aud` set to the user pool client's ID, so API Gateway's
 * HttpJwtAuthorizer validates either without config changes.
 */
export async function getValidIdToken(): Promise<string | null> {
  const token = getIdToken();
  if (!token) return null;
  if (!isTokenExpired(token)) return token;
  const refreshed = await refreshAccessToken();
  return refreshed ? getIdToken() : null;
}

export function isAuthenticated(): boolean {
  if (getConfig().environment === 'local') return true;
  const token = getAccessToken();
  if (!token) return false;
  try {
    const payload = parseJwt(token);
    return !!payload.exp && payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

export interface UserInfo {
  email: string;
  givenName: string;
  familyName: string;
  sub: string;
}

export function getUserInfo(): UserInfo | null {
  if (getConfig().environment === 'local') {
    return { email: 'dev@local', givenName: 'Local', familyName: 'Dev', sub: 'local' };
  }
  const token = getIdToken();
  if (!token) return null;
  try {
    const payload = parseJwt(token);
    return {
      email: (payload.email as string) ?? '',
      givenName: (payload.given_name as string) ?? '',
      familyName: (payload.family_name as string) ?? '',
      sub: (payload.sub as string) ?? '',
    };
  } catch {
    return null;
  }
}

export function logout(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(ID_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  if (getConfig().environment !== 'local') {
    window.location.assign(getLogoutUrl());
  } else {
    window.location.assign('/');
  }
}
